import type { Position } from "@turf/helpers";

import type { XdaDucWell } from "models/xdaData";

/**
 * EVA-185 — dot-only wells ("DUC Dots").
 *
 * The XDA service returns, in `duc_wells`, the surveyed wells that cross the
 * section line and that the drainage math excludes: no production recorded, no
 * DRAIN in the status, not SAGD, and no injection date, minus anything
 * abandoned. So DUCs, idle, suspended and shut-in wells qualify, while DRAIN
 * and injector wells never do - they keep their bubbles. They are
 * drawn as plain dots: no polygon, no entry in `ids` / `centroids` /
 * `well_data`, no part in the XDA math or the spacing measurement. The service
 * only fills the array when the request carried `showDucWells`, so a non-empty
 * array IS the toggle being on — nothing here re-checks the setting.
 */

/**
 * Black, the same fill `drawCentroid` gives a well dot when Well Data is off —
 * a dot-well is meant to read as a well with no bubble, not as its own species.
 * The ticket's orange belongs to the MAP marker, which reuses the existing
 * orange XDA intercept layer; only the gunbarrel dot is black. (EVA-185)
 */
export const DUC_DOT_COLOR = "#212121";
export const DUC_DOT_RADIUS = 3;

/** Apply the Scale Width factor to x only, exactly as `scaleData` does for centroids. */
export function scaleDucWells(
  ducWells: readonly XdaDucWell[] | undefined,
  scale: number
): XdaDucWell[] {
  return (ducWells ?? []).map((duc) => ({
    ...duc,
    point: { x: duc.point.x * scale, y: duc.point.y }
  }));
}

/** Map-marker positions ([lon, lat]) for the dot-wells. */
export function ducInterceptPositions(
  ducWells: readonly XdaDucWell[] | undefined
): Position[] {
  return (ducWells ?? []).map((duc) => [duc.intercept.x, duc.intercept.y]);
}

/** Distinct UWIs of the dot-wells, in response order. */
export function collectDucWellUwis(
  ducWells: readonly XdaDucWell[] | undefined
): string[] {
  return Array.from(new Set((ducWells ?? []).map((duc) => duc.unique_id)));
}

/**
 * The well list "Filter to wells" applies: the section's wells plus the
 * dot-wells, deduplicated and in that order.
 */
export function mergeFilterWellList(
  selectedUwis: readonly string[],
  additionalUwis: readonly string[] = []
): string[] {
  return Array.from(new Set([...selectedUwis, ...additionalUwis]));
}
